import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/query-client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown, AlertTriangle, ShieldCheck, Edit, Loader2, CheckCircle } from "lucide-react";
import { formatDateTime } from "@/lib/format-utils";

interface LeadAIFeedbackBarProps {
  leadId: string;
  feature: string;
  interactionId?: string;
  originalResult: any;
  className?: string;
  title?: string;
}

export function LeadAIFeedbackBar({ 
  leadId, 
  feature, 
  interactionId, 
  originalResult,
  className = "",
  title = "AI Analysis"
}: LeadAIFeedbackBarProps) {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [justSubmitted, setJustSubmitted] = useState<'confirmed' | 'corrected' | null>(null);
  const [wantsOverride, setWantsOverride] = useState(false);

  // Skip rendering if no interaction context is provided
  if (!interactionId) return null;

  // Query existing corrections for this lead
  const { data: feedbackData } = useQuery<any>({
    queryKey: ['/api/leads', leadId, 'ai-feedback'],
    queryFn: async () => {
      const response = await fetch(`/api/leads/${leadId}/ai-feedback`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!leadId,
  });

  // Find the latest correction for this specific interaction and feature
  const matchingFeedback = feedbackData && Array.isArray(feedbackData) 
    ? feedbackData.filter((f: any) => f.metadata.leadId === interactionId )
    : [];
  
  const latestCorrection = matchingFeedback.length > 0 ? matchingFeedback[0] : null;

  console.log('batman!');
  console.log(interactionId);
  console.log(feedbackData);
  console.log(matchingFeedback);
  console.log(latestCorrection);
  
  const feedbackStatus = wantsOverride ? null : (justSubmitted || (
    latestCorrection?.correctionType === 'confirmation' ? 'confirmed' :
    latestCorrection?.correctionType === 'correction' ? 'corrected' :
    null
  ));
  console.log(feedbackStatus);

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', `/api/leads/${leadId}/ai-feedback`, {
        feature,
        interactionId,
        feedbackType: 'confirmation',
        originalResult,
        correctedResult: originalResult, // For confirmation they are identical
        notes: 'User verified AI result',
      });
      return response.json();
    },
    onSuccess: () => {
      setJustSubmitted('confirmed');
      setWantsOverride(false);
      queryClient.invalidateQueries({ queryKey: ['/api/leads', leadId, 'ai-feedback'] });
      toast({ title: '✅ Analysis confirmed as correct', description: 'This helps improve future analyses' });
    },
    onError: () => {
      toast({ title: 'Failed to confirm', variant: 'destructive' });
    },
  });

  const correctMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', `/api/leads/${leadId}/ai-feedback`, {
        feature,
        interactionId,
        feedbackType: 'correction',
        originalResult,
        correctedResult: null, // Depending on the implementation, we might provide full partial objects
        notes: correctionNotes || undefined,
      });
      return response.json();
    },
    onSuccess: () => {
      setJustSubmitted('corrected');
      setWantsOverride(false);
      queryClient.invalidateQueries({ queryKey: ['/api/leads', leadId, 'ai-feedback'] });
      toast({ title: '📝 Correction submitted', description: 'Your feedback will improve future analyses' });
      setShowForm(false);
      setCorrectionNotes('');
    },
    onError: () => {
      toast({ title: 'Failed to submit correction', variant: 'destructive' });
    },
  });

  if (feedbackStatus === 'confirmed') {
    return (
      <div className={`w-full flex items-center gap-2 p-2.5 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 ${className}`}>
        <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-green-700 dark:text-green-400">{title} verified as correct</p>
          {latestCorrection?.createdAt && (
            <p className="text-[10px] text-green-600/60 dark:text-green-400/80">
              {formatDateTime(latestCorrection.createdAt)}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[10px] text-muted-foreground px-2"
          onClick={() => setWantsOverride(true)}
        >
          Change
        </Button>
      </div>
    );
  }

  if (feedbackStatus === 'corrected') {
    return (
      <div className={`w-full space-y-1.5 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 ${className}`}>
        <div className="flex items-center gap-2">
          <Edit className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 flex-1">{title} corrected</p>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[10px] text-muted-foreground px-2"
            onClick={() => setWantsOverride(true)}
          >
            Change
          </Button>
        </div>
        {latestCorrection?.notes && (
          <div className="text-[11px] text-amber-700/80 dark:text-amber-400/80 pl-6">
            <p className="italic">"{latestCorrection.notes}"</p>
          </div>
        )}
        {latestCorrection?.createdAt && (
          <p className="text-[10px] text-amber-600/50 dark:text-amber-400/80 pl-6">
            {formatDateTime(latestCorrection.createdAt)}
          </p>
        )}
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className={`flex items-center gap-2 mt-2 ${className}`}>
        <p className="text-xs text-muted-foreground flex-1">Rate this {title.toLowerCase()}:</p>
        <Button
          variant="outline"
          size="sm"
          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 h-7"
          onClick={() => confirmMutation.mutate()}
          disabled={confirmMutation.isPending}
        >
          {confirmMutation.isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <ThumbsUp className="h-3 w-3 mr-1" />
          )}
          Correct
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 h-7"
          onClick={() => setShowForm(true)}
        >
          <ThumbsDown className="h-3 w-3 mr-1" />
          Incorrect
        </Button>
      </div>
    );
  }

  return (
    <div className={`w-full space-y-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 mt-2 ${className}`}>
      <div className="text-xs font-medium flex items-center gap-1">
        <AlertTriangle className="h-3 w-3 text-amber-600" />
        Correct AI {title}
      </div>
      <Textarea
        placeholder="Why was the AI wrong? What should it have been? (e.g., 'The valuation was for full replacement, not just ACV')"
        value={correctionNotes}
        onChange={(e) => setCorrectionNotes(e.target.value)}
        className="text-xs min-h-[60px]"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-7"
          onClick={() => setShowForm(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="flex-1 h-7"
          onClick={() => correctMutation.mutate()}
          disabled={correctMutation.isPending || !correctionNotes.trim()}
        >
          {correctMutation.isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <CheckCircle className="h-3 w-3 mr-1" />
          )}
          Submit Correction
        </Button>
      </div>
    </div>
  );
}
